import { ChangeDetectionStrategy, Component, computed, inject, Input, Signal } from '@angular/core';
import { OrAvatarComponent, OrIconComponent, SingleRadioItemModel } from '@ui-components';
import {
  ActionModalComponent,
  ButtonLabels,
  GameCommentModel,
  GamePenaltyModel,
  GamePlayerModel,
  GlobalRefreshService,
  hostRemovalReasonMapper,
  MODAL_TOKEN,
  ModalProvider,
  PresentModalComponent,
  TOAST_TOKEN,
  ToastProvider
} from '@core';
import {
  MarkAsPaidCommandHandler,
  RemovePlayerCommandHandler,
  RemovePlayerCommandPayload
} from '../../application/handlers';
import { HostedGamesService } from '@hosted-games/infra';
import { forkJoin, Observable, take } from 'rxjs';
import { PaymentConfirmCheckboxComponent } from '../payment-confirm-checkbox/payment-confirm-checkbox.component';
import { HIDE_PAYMENTS_MODAL } from '../../application/utils';
import { AddCustomPlayerComponent } from '../add-custom-player/add-custom-player.component';
import {
  AddCardModalComponent,
  AddCommentModalComponent,
  cardReasonMapper,
  CardReasonModel,
  RevokeCardCommandHandler,
  RevokeCommentCommandHandler
} from '@fair-play';
import { PenaltiesService, UserCommentsService } from '@fair-play/infra';
import { HostRemovalReason, PenaltyReason } from '@core-http';
import { RemovePlayerReasonComponent } from '../remove-player-reason/remove-player-reason.component';

@Component({
  selector: 'lib-manage-player',
  templateUrl: './manage-player.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [OrAvatarComponent, OrIconComponent],
  providers: [
    RemovePlayerCommandHandler,
    MarkAsPaidCommandHandler,
    HostedGamesService,
    RevokeCardCommandHandler,
    RevokeCommentCommandHandler,
    PenaltiesService,
    UserCommentsService
  ]
})
export class ManagePlayerComponent {
  @Input() player!: GamePlayerModel;
  @Input() gameId!: string;
  @Input() isPlayerInGame: boolean = true;

  private readonly removePlayerCommandHandler: RemovePlayerCommandHandler = inject(RemovePlayerCommandHandler);
  private readonly markAsPaidCommandHandler: MarkAsPaidCommandHandler = inject(MarkAsPaidCommandHandler);
  private readonly revokeCardCommandHandler: RevokeCardCommandHandler = inject(RevokeCardCommandHandler);
  private readonly revokeCommentCommandHandler: RevokeCommentCommandHandler = inject(RevokeCommentCommandHandler);
  private readonly modalProvider: ModalProvider = inject(MODAL_TOKEN);
  private readonly toastProvider: ToastProvider = inject(TOAST_TOKEN);
  private readonly globalRefreshService: GlobalRefreshService = inject(GlobalRefreshService);

  readonly cardReasonMapper: Record<PenaltyReason, CardReasonModel> = cardReasonMapper;

  private readonly removalReasons: SingleRadioItemModel<HostRemovalReason>[] = [
    HostRemovalReason.MISMATCHED_LEVEL,
    HostRemovalReason.INCOMPLETE_PROFILE,
    HostRemovalReason.FRIENDS_ONLY,
    HostRemovalReason.LOOKING_FOR_OTHER_POSITION,
    HostRemovalReason.PREFER_NOT_TO_SAY
  ].map((value: HostRemovalReason) => ({ value, name: hostRemovalReasonMapper[value] }));

  readonly revokableCard: Signal<GamePenaltyModel | undefined> = computed(() =>
    this.player?.penalties?.find((penalty: GamePenaltyModel) => penalty.canRevoke && !penalty.revokedAt)
  );

  readonly revokableComments: Signal<GameCommentModel[]> = computed(
    () => this.player?.comments?.filter((comment: GameCommentModel) => comment.canRevoke) ?? []
  );

  get paymentStatusText(): ButtonLabels {
    const header: string = this.player.isPaid ? 'Oznacz jako niezapłacone' : 'Oznacz jako zapłacone';

    const text: string = this.player.isPaid
      ? 'Czy na pewno chcesz oznaczyć jako niezapłacone?'
      : 'Czy na pewno chcesz oznaczyć jako zapłacone?';
    return { header, text };
  }

  removePlayer(isPaid: boolean): Observable<void> {
    if (isPaid)
      return this.modalProvider.showModal$({
        component: PresentModalComponent,
        cssClass: 'present-modal',
        componentProps: {
          header: 'Usunięcie gracza',
          message: 'Nie możesz usunąć gracza, który już zapłacił',
          btnTxt: 'Zamknij'
        }
      });

    if (!this.player.userId) return this.showRemoveWithoutReasonModal();

    return this.showRemoveWithReasonModal();
  }

  private showRemoveWithoutReasonModal(): Observable<void> {
    return this.modalProvider.showModal$({
      component: ActionModalComponent,
      cssClass: 'present-modal',
      componentProps: {
        header: 'Usuń gracza z gierki',
        message: 'Czy na pewno chcesz usunąć gracza z gierki?',
        btnOk: 'Usuń gracza',
        btnCancel: 'Anuluj',
        action: () => this.executeRemovePlayer()
      }
    });
  }

  private showRemoveWithReasonModal(): Observable<void> {
    return this.modalProvider.showModal$({
      component: ActionModalComponent,
      cssClass: 'present-modal',
      componentProps: {
        header: 'Usuń gracza z gierki',
        message: 'Wybierz powód - gracz dowie się o nim w powiadomieniu:',
        btnOk: 'Usuń gracza',
        btnCancel: 'Anuluj',
        template: RemovePlayerReasonComponent,
        templateInputs: { reasons: this.removalReasons },
        requireTemplateSelection: true,
        action: (reason?: unknown) => this.executeRemovePlayer(reason as HostRemovalReason)
      }
    });
  }

  private executeRemovePlayer(removalReason?: HostRemovalReason): void {
    const payload: RemovePlayerCommandPayload = {
      gameId: this.gameId,
      playerId: this.player.playerId,
      removalReason: removalReason
    };

    this.removePlayerCommandHandler
      .remove(payload)
      .pipe(take(1))
      .subscribe(() => this.toastProvider.showToast({ message: 'Gracz został usunięty' }));
  }

  changePaymentStatus(): void {
    const hideModal: { isHide: boolean } = JSON.parse(localStorage.getItem(HIDE_PAYMENTS_MODAL) as string);

    if (hideModal?.isHide) {
      this.modalProvider.dismissAllStoredModals();
      return this.markAsPaid();
    }

    this.modalProvider.showModal$({
      component: ActionModalComponent,
      cssClass: 'present-modal',
      componentProps: {
        header: this.paymentStatusText.header,
        message: this.paymentStatusText.text,
        btnOk: 'Tak, potwierdzam',
        btnCancel: 'Anuluj',
        template: PaymentConfirmCheckboxComponent,
        action: () => this.markAsPaid()
      }
    });
  }

  private markAsPaid(): void {
    const wasPaid: boolean = this.player.isPaid!;

    this.markAsPaidCommandHandler
      .markAsPaid(this.gameId, this.player.playerId, wasPaid)
      .pipe(take(1))
      .subscribe(() => {
        if (wasPaid) {
          this.toastProvider.showToast({ message: 'Cofnięto oznaczenie wpłaty' });
        }
      });
  }

  editPlayer(): void {
    this.modalProvider
      .showModal$({
        component: AddCustomPlayerComponent,
        cssClass: 'present-modal',
        componentProps: {
          gameId: this.gameId,
          player: this.player,
          isEdit: true
        }
      })
      .subscribe(() => this.modalProvider.dismissAllStoredModals());
  }

  addCard(): void {
    this.modalProvider
      .showModal$({
        component: AddCardModalComponent,
        cssClass: 'present-modal',
        componentProps: {
          gameId: this.gameId,
          player: this.player
        }
      })
      .pipe(take(1))
      .subscribe(() => this.modalProvider.dismissAllStoredModals());
  }

  addComment(): void {
    this.modalProvider
      .showModal$({
        component: AddCommentModalComponent,
        cssClass: 'present-modal',
        componentProps: {
          gameId: this.gameId,
          player: this.player
        }
      })
      .pipe(take(1))
      .subscribe(() => this.modalProvider.dismissAllStoredModals());
  }

  revokeCard(): void {
    const card: GamePenaltyModel | undefined = this.revokableCard();
    if (!card?.userPenaltyId) return;

    this.modalProvider.showModal$({
      component: ActionModalComponent,
      cssClass: 'present-modal',
      componentProps: {
        header: 'Cofnij kartkę',
        message: 'Czy na pewno chcesz cofnąć kartkę przyznaną za tę grę?',
        btnOk: 'Tak, cofnij kartkę',
        btnCancel: 'Anuluj',
        action: () => this.executeRevokeCard(card.userPenaltyId!)
      }
    });
  }

  revokeComment(): void {
    const comments: GameCommentModel[] = this.revokableComments();
    if (!comments.length) return;

    this.modalProvider.showModal$({
      component: ActionModalComponent,
      cssClass: 'present-modal',
      componentProps: {
        header: 'Cofnij komplement',
        message: 'Czy na pewno chcesz cofnąć komplement przyznany za tę grę?',
        btnOk: 'Tak, cofnij komplement',
        btnCancel: 'Anuluj',
        action: () => this.executeRevokeComment(comments)
      }
    });
  }

  private executeRevokeCard(userPenaltyId: string): void {
    this.revokeCardCommandHandler
      .revoke({ userPenaltyId })
      .pipe(take(1))
      .subscribe(() => {
        this.globalRefreshService.emit();
        this.toastProvider.showToast({ message: 'Kartka została cofnięta' });
      });
  }

  private executeRevokeComment(comments: GameCommentModel[]): void {
    forkJoin(
      comments.map((comment: GameCommentModel) =>
        this.revokeCommentCommandHandler.revoke({ commentId: comment.commentId }).pipe(take(1))
      )
    )
      .pipe(take(1))
      .subscribe(() => {
        this.globalRefreshService.emit();
        this.toastProvider.showToast({ message: 'Komplement został cofnięty' });
      });
  }
}
